import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/**
 * AIRA VOCAL - Internal Voice Engine for AIRA ECHO
 * 
 * Hybrid Architecture:
 * - Server-side TTS using Piper TTS for high-quality consistent voice output
 * - Browser Web Speech API for real-time STT (speech recognition)
 * - Multiple voice profiles with age, gender, and accent customization
 * - Language-based automatic voice selection
 * 
 * Features:
 * - High-quality text-to-speech via AIRA VOCAL backend
 * - Accurate voice-to-text via Web Speech API
 * - 5 pre-defined voice personas + full customization
 * - Speed, pitch, and volume controls
 * - Real-time speaking state for AI Brain visualizer
 * - Multi-language support (18+ languages)
 */

const VoiceContext = createContext(null);

// Voice Providers - AIRA VOCAL is the primary provider
export const VOICE_PROVIDERS = {
  AIRA_VOCAL: 'aira_vocal',  // Primary: Server-side Piper TTS
  BROWSER: 'browser',         // Fallback: Browser Web Speech API
  HYBRID: 'hybrid'            // Use both for best experience
};

// Supported Languages with Web Speech API codes
export const SUPPORTED_LANGUAGES = {
  en: { id: 'en', code: 'en-US', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  hi: { id: 'hi', code: 'hi-IN', name: 'Hindi', nativeName: 'हिंदी', flag: '🇮🇳' },
  ur: { id: 'ur', code: 'ur-PK', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰' },
  es: { id: 'es', code: 'es-ES', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  ja: { id: 'ja', code: 'ja-JP', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  ru: { id: 'ru', code: 'ru-RU', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  bn: { id: 'bn', code: 'bn-IN', name: 'Bengali', nativeName: 'বাংলা', flag: '🇮🇳' },
  mr: { id: 'mr', code: 'mr-IN', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳' },
  pa: { id: 'pa', code: 'pa-IN', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  ta: { id: 'ta', code: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
  de: { id: 'de', code: 'de-DE', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  te: { id: 'te', code: 'te-IN', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  ml: { id: 'ml', code: 'ml-IN', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳' },
  ar: { id: 'ar', code: 'ar-SA', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  fr: { id: 'fr', code: 'fr-FR', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  pt: { id: 'pt', code: 'pt-BR', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  zh: { id: 'zh', code: 'zh-CN', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  ko: { id: 'ko', code: 'ko-KR', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
};

// Voice Personality Profiles - AIRA VOCAL Personas
export const VOICE_PERSONALITIES = {
  aira_young_female_us: {
    id: 'aira_young_female_us',
    name: 'AIRA Young Female',
    description: 'Energetic, friendly young female voice with US accent',
    rate: 1.1,
    pitch: 1.05,
    preferredVoice: 'female',
    accent: 'us',
    age: 'young',
    style: 'energetic'
  },
  aira_professional_male_uk: {
    id: 'aira_professional_male_uk',
    name: 'AIRA Professional Male',
    description: 'Clear, professional male voice with UK accent',
    rate: 1.0,
    pitch: 0.9,
    preferredVoice: 'male',
    accent: 'uk',
    age: 'adult',
    style: 'professional'
  },
  aira_friendly_female_indian: {
    id: 'aira_friendly_female_indian',
    name: 'AIRA Friendly Female',
    description: 'Warm, approachable female voice with Indian English accent',
    rate: 0.95,
    pitch: 1.0,
    preferredVoice: 'female',
    accent: 'indian',
    age: 'adult',
    style: 'friendly'
  },
  aira_futuristic_neutral: {
    id: 'aira_futuristic_neutral',
    name: 'AIRA Futuristic',
    description: 'Calm, precise, slightly futuristic neutral voice',
    rate: 0.9,
    pitch: 0.95,
    preferredVoice: 'neutral',
    accent: 'neutral',
    age: 'adult',
    style: 'futuristic'
  },
  aira_calm_mature: {
    id: 'aira_calm_mature',
    name: 'AIRA Calm Mature',
    description: 'Relaxed, soothing mature voice',
    rate: 0.85,
    pitch: 0.9,
    preferredVoice: 'neutral',
    accent: 'us',
    age: 'mature',
    style: 'calm'
  }
};

// Accent profiles (for English)
export const VOICE_ACCENTS = {
  us: { id: 'us', name: 'US English', lang: 'en-US', flag: '🇺🇸' },
  uk: { id: 'uk', name: 'UK English', lang: 'en-GB', flag: '🇬🇧' },
  indian: { id: 'indian', name: 'Indian English', lang: 'en-IN', flag: '🇮🇳' },
  spanish: { id: 'spanish', name: 'Spanish', lang: 'es-ES', flag: '🇪🇸' },
  arabic: { id: 'arabic', name: 'Arabic', lang: 'ar-SA', flag: '🇸🇦' },
  neutral: { id: 'neutral', name: 'Neutral', lang: 'en-US', flag: '🌐' }
};

// Voice age groups
export const VOICE_AGE_GROUPS = {
  young: { id: 'young', name: 'Young', description: 'Energetic, youthful voice' },
  adult: { id: 'adult', name: 'Adult', description: 'Balanced, professional voice' },
  mature: { id: 'mature', name: 'Mature', description: 'Calm, experienced voice' }
};

// Voice gender options
export const VOICE_GENDERS = {
  female: { id: 'female', name: 'Female', icon: '👩' },
  male: { id: 'male', name: 'Male', icon: '👨' },
  neutral: { id: 'neutral', name: 'Neutral', icon: '🤖' }
};

// Find preferred voice based on accent and gender (pure function, doesn't need component state)
const findPreferredVoice = (voices, accentId, genderId) => {
  if (!voices || voices.length === 0) return -1;
  
  const accentConfig = VOICE_ACCENTS[accentId];
  const lang = accentConfig?.lang || 'en-US';
  
  // Filter voices by language
  const matchingVoices = voices.filter(v => v.lang.startsWith(lang.split('-')[0]));
  
  if (matchingVoices.length === 0) return 0;
  
  // Try to match gender (heuristic based on voice name)
  const genderKeywords = {
    female: ['female', 'woman', 'girl', 'samantha', 'victoria', 'karen', 'moira', 'fiona', 'susan', 'zira', 'hazel'],
    male: ['male', 'man', 'boy', 'david', 'daniel', 'james', 'mark', 'alex', 'tom', 'george'],
    neutral: ['google', 'microsoft', 'neural', 'standard']
  };
  
  const keywords = genderKeywords[genderId] || genderKeywords.neutral;
  
  for (const keyword of keywords) {
    const match = matchingVoices.findIndex(v => 
      v.name.toLowerCase().includes(keyword)
    );
    if (match !== -1) {
      return voices.indexOf(matchingVoices[match]);
    }
  }
  
  // Return first matching language voice
  return voices.indexOf(matchingVoices[0]);
};

export function VoiceProvider({ children }) {
  // Voice settings state
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [provider, setProvider] = useState(VOICE_PROVIDERS.HYBRID);
  const [personality, setPersonality] = useState('aira_young_female_us');
  const [accent, setAccent] = useState('us');
  const [gender, setGender] = useState('female');
  const [age, setAge] = useState('adult');
  const [rate, setRate] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  
  // AIRA VOCAL status
  const [airaVocalAvailable, setAiraVocalAvailable] = useState(false);
  const [airaVocalLoading, setAiraVocalLoading] = useState(false);
  
  // Language settings
  const [language, setLanguage] = useState('en');
  const [autoDetectLanguage, setAutoDetectLanguage] = useState(true);
  const [detectedLanguage, setDetectedLanguage] = useState(null);
  
  // Always Listening Mode (Wake Word Detection)
  const [alwaysListening, setAlwaysListening] = useState(false);
  const [wakeWord, setWakeWord] = useState('hey aira');
  const [wakeWordDetected, setWakeWordDetected] = useState(false);
  const [isWakeWordListening, setIsWakeWordListening] = useState(false);
  const wakeWordRecognitionRef = useRef(null);
  const onWakeWordDetectedRef = useRef(null);
  
  // Speaking state for visualizer
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingProgress, setSpeakingProgress] = useState(0);
  const [currentText, setCurrentText] = useState('');
  
  // Available voices from browser
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  
  // Audio refs for AIRA VOCAL
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Refs
  const synthRef = useRef(null);
  const utteranceRef = useRef(null);
  const onSpeakingChangeRef = useRef(null);
  
  // API URL
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check AIRA VOCAL availability on mount
  useEffect(() => {
    const checkAiraVocal = async () => {
      try {
        const response = await fetch(`${API_URL}/api/vocal/status`);
        if (response.ok) {
          const data = await response.json();
          setAiraVocalAvailable(data.piper_available && data.available_voices?.length > 0);
          console.log('AIRA VOCAL Status:', data);
        }
      } catch (error) {
        console.log('AIRA VOCAL not available, using browser fallback');
        setAiraVocalAvailable(false);
      }
    };
    checkAiraVocal();
  }, [API_URL]);

  // Get current language config
  const getCurrentLanguage = useCallback(() => {
    const langId = autoDetectLanguage && detectedLanguage ? detectedLanguage : language;
    return SUPPORTED_LANGUAGES[langId] || SUPPORTED_LANGUAGES.en;
  }, [language, autoDetectLanguage, detectedLanguage]);

  // Get language code for Web Speech API
  const getSpeechRecognitionLanguage = useCallback(() => {
    return getCurrentLanguage().code;
  }, [getCurrentLanguage]);

  // Wake Word Detection callback
  const onWakeWordDetected = useCallback((callback) => {
    onWakeWordDetectedRef.current = callback;
  }, []);

  // Start Wake Word Listening
  const startWakeWordListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported');
      return;
    }

    if (wakeWordRecognitionRef.current) {
      wakeWordRecognitionRef.current.abort();
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    wakeWordRecognitionRef.current = new SpeechRecognition();
    wakeWordRecognitionRef.current.continuous = true;
    wakeWordRecognitionRef.current.interimResults = true;
    wakeWordRecognitionRef.current.lang = getSpeechRecognitionLanguage();

    wakeWordRecognitionRef.current.onstart = () => {
      setIsWakeWordListening(true);
      console.log('Wake word listening started...');
    };

    wakeWordRecognitionRef.current.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.toLowerCase().trim();
        
        // Check for wake word variations
        const wakeWords = [
          wakeWord.toLowerCase(),
          'hey aira',
          'hi aira',
          'hello aira',
          'aira',
          'hey ira',
          'hey era'
        ];
        
        const detected = wakeWords.some(w => transcript.includes(w));
        
        if (detected) {
          console.log('Wake word detected:', transcript);
          setWakeWordDetected(true);
          
          // Stop wake word listening temporarily
          if (wakeWordRecognitionRef.current) {
            wakeWordRecognitionRef.current.stop();
          }
          
          // Trigger callback
          if (onWakeWordDetectedRef.current) {
            onWakeWordDetectedRef.current(transcript);
          }
          
          // Reset wake word detected after a delay
          setTimeout(() => {
            setWakeWordDetected(false);
            // Restart wake word listening if still enabled
            if (alwaysListening) {
              startWakeWordListening();
            }
          }, 5000);
          
          break;
        }
      }
    };

    wakeWordRecognitionRef.current.onerror = (event) => {
      console.log('Wake word recognition error:', event.error);
      if (event.error === 'no-speech' || event.error === 'aborted') {
        // Restart if still enabled
        if (alwaysListening) {
          setTimeout(() => startWakeWordListening(), 1000);
        }
      }
      setIsWakeWordListening(false);
    };

    wakeWordRecognitionRef.current.onend = () => {
      setIsWakeWordListening(false);
      // Auto-restart if always listening is enabled and not currently processing
      if (alwaysListening && !wakeWordDetected) {
        setTimeout(() => startWakeWordListening(), 500);
      }
    };

    try {
      wakeWordRecognitionRef.current.start();
    } catch (e) {
      console.error('Failed to start wake word recognition:', e);
    }
  }, [alwaysListening, wakeWord, wakeWordDetected, getSpeechRecognitionLanguage]);

  // Stop Wake Word Listening
  const stopWakeWordListening = useCallback(() => {
    if (wakeWordRecognitionRef.current) {
      wakeWordRecognitionRef.current.abort();
      wakeWordRecognitionRef.current = null;
    }
    setIsWakeWordListening(false);
    setWakeWordDetected(false);
  }, []);

  // Toggle Always Listening Mode
  const toggleAlwaysListening = useCallback((enabled) => {
    setAlwaysListening(enabled);
    if (enabled) {
      startWakeWordListening();
    } else {
      stopWakeWordListening();
    }
  }, [startWakeWordListening, stopWakeWordListening]);

  // Effect to manage always listening state
  useEffect(() => {
    if (alwaysListening) {
      startWakeWordListening();
    } else {
      stopWakeWordListening();
    }
    
    return () => {
      stopWakeWordListening();
    };
  }, [alwaysListening, startWakeWordListening, stopWakeWordListening]);

  // Initialize speech synthesis
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
      
      // Load available voices
      const loadVoices = () => {
        const voices = synthRef.current.getVoices();
        setAvailableVoices(voices);
        
        // Try to select a default voice based on settings
        const preferredVoice = findPreferredVoice(voices, accent, gender);
        if (preferredVoice !== -1) {
          setSelectedVoiceIndex(preferredVoice);
        }
      };
      
      loadVoices();
      synthRef.current.onvoiceschanged = loadVoices;
      
      // Load saved settings from localStorage
      const savedSettings = localStorage.getItem('aira-voice-settings');
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.voiceEnabled !== undefined) setVoiceEnabled(settings.voiceEnabled);
          if (settings.personality) setPersonality(settings.personality);
          if (settings.accent) setAccent(settings.accent);
          if (settings.gender) setGender(settings.gender);
          if (settings.rate) setRate(settings.rate);
          if (settings.pitch) setPitch(settings.pitch);
          if (settings.volume) setVolume(settings.volume);
          if (settings.language) setLanguage(settings.language);
          if (settings.autoDetectLanguage !== undefined) setAutoDetectLanguage(settings.autoDetectLanguage);
          if (settings.wakeWord) setWakeWord(settings.wakeWord);
          // Note: alwaysListening is NOT auto-loaded for privacy - user must enable each session
        } catch (e) {
          console.error('Failed to load voice settings:', e);
        }
      }
    }
    
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - initialization only, accent/gender changes handled separately

  // Save settings to localStorage when they change
  useEffect(() => {
    const settings = {
      voiceEnabled,
      personality,
      accent,
      gender,
      rate,
      pitch,
      volume,
      language,
      autoDetectLanguage,
      wakeWord
      // Note: alwaysListening is NOT saved for privacy - user must enable each session
    };
    localStorage.setItem('aira-voice-settings', JSON.stringify(settings));
  }, [voiceEnabled, personality, accent, gender, rate, pitch, volume, language, autoDetectLanguage, wakeWord]);

  // Update voice selection when accent/gender changes
  useEffect(() => {
    if (availableVoices.length > 0) {
      const preferredVoice = findPreferredVoice(availableVoices, accent, gender);
      if (preferredVoice !== -1) {
        setSelectedVoiceIndex(preferredVoice);
      }
    }
  }, [accent, gender, availableVoices]);

  // Apply personality preset
  const applyPersonality = useCallback((personalityId) => {
    const preset = VOICE_PERSONALITIES[personalityId];
    if (preset) {
      setPersonality(personalityId);
      setRate(preset.rate);
      setPitch(preset.pitch);
      if (preset.preferredVoice) {
        setGender(preset.preferredVoice);
      }
      if (preset.accent) {
        setAccent(preset.accent);
      }
      if (preset.age) {
        setAge(preset.age);
      }
    }
  }, []);

  // AIRA VOCAL TTS - Server-side high-quality synthesis
  const speakWithAiraVocal = useCallback(async (text, options = {}) => {
    if (!voiceEnabled) {
      return { spoken: false, reason: 'disabled' };
    }

    setAiraVocalLoading(true);
    setIsSpeaking(true);
    setCurrentText(text);
    setSpeakingProgress(0);
    onSpeakingChangeRef.current?.(true, text);

    try {
      const response = await fetch(`${API_URL}/api/vocal/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          profile_id: personality,
          language: language,
          accent: accent,
          gender: gender,
          age: age,
          speed: rate,
          pitch: pitch
        })
      });

      if (!response.ok) {
        throw new Error('AIRA VOCAL synthesis failed');
      }

      const data = await response.json();
      
      if (!data.success || !data.audio) {
        throw new Error('No audio data received');
      }

      // Convert base64 to audio blob
      const audioBytes = atob(data.audio);
      const audioArray = new Uint8Array(audioBytes.length);
      for (let i = 0; i < audioBytes.length; i++) {
        audioArray[i] = audioBytes.charCodeAt(i);
      }
      const audioBlob = new Blob([audioArray], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);

      // Play audio
      return new Promise((resolve) => {
        const audio = new Audio(audioUrl);
        audio.volume = volume;
        audioRef.current = audio;

        audio.onplay = () => {
          setSpeakingProgress(0.1);
        };

        audio.ontimeupdate = () => {
          if (audio.duration) {
            setSpeakingProgress(audio.currentTime / audio.duration);
          }
        };

        audio.onended = () => {
          setIsSpeaking(false);
          setCurrentText('');
          setSpeakingProgress(1);
          setAiraVocalLoading(false);
          onSpeakingChangeRef.current?.(false, '');
          URL.revokeObjectURL(audioUrl);
          resolve({ spoken: true, provider: 'aira_vocal' });
        };

        audio.onerror = () => {
          setIsSpeaking(false);
          setCurrentText('');
          setAiraVocalLoading(false);
          onSpeakingChangeRef.current?.(false, '');
          URL.revokeObjectURL(audioUrl);
          resolve({ spoken: false, reason: 'playback_error' });
        };

        audio.play().catch(() => {
          setIsSpeaking(false);
          setAiraVocalLoading(false);
          resolve({ spoken: false, reason: 'autoplay_blocked' });
        });
      });

    } catch (error) {
      console.error('AIRA VOCAL error:', error);
      setIsSpeaking(false);
      setCurrentText('');
      setAiraVocalLoading(false);
      onSpeakingChangeRef.current?.(false, '');
      return { spoken: false, reason: error.message };
    }
  }, [voiceEnabled, API_URL, personality, language, accent, gender, age, rate, pitch, volume]);

  // Browser TTS fallback
  const speakWithBrowser = useCallback((text, options = {}) => {
    return new Promise((resolve, reject) => {
      if (!voiceEnabled || !synthRef.current) {
        resolve({ spoken: false, reason: 'disabled' });
        return;
      }

      // Cancel any ongoing speech
      synthRef.current.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utteranceRef.current = utterance;

      // Apply settings
      const personalityConfig = VOICE_PERSONALITIES[personality];
      const finalRate = options.rate || rate * (personalityConfig?.rate || 1);
      const finalPitch = options.pitch || pitch * (personalityConfig?.pitch || 1);
      
      utterance.rate = Math.max(0.1, Math.min(2, finalRate));
      utterance.pitch = Math.max(0, Math.min(2, finalPitch));
      utterance.volume = Math.max(0, Math.min(1, volume));

      // Set voice
      if (availableVoices.length > 0 && selectedVoiceIndex < availableVoices.length) {
        utterance.voice = availableVoices[selectedVoiceIndex];
      }

      // Events
      utterance.onstart = () => {
        setIsSpeaking(true);
        setCurrentText(text);
        setSpeakingProgress(0);
        onSpeakingChangeRef.current?.(true, text);
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setCurrentText('');
        setSpeakingProgress(1);
        onSpeakingChangeRef.current?.(false, '');
        resolve({ spoken: true, provider: 'browser' });
      };

      utterance.onerror = (event) => {
        setIsSpeaking(false);
        setCurrentText('');
        onSpeakingChangeRef.current?.(false, '');
        
        if (event.error === 'canceled') {
          resolve({ spoken: false, reason: 'canceled' });
        } else {
          reject(new Error(event.error));
        }
      };

      utterance.onboundary = (event) => {
        if (event.name === 'word') {
          const progress = event.charIndex / text.length;
          setSpeakingProgress(progress);
        }
      };

      // Start speaking
      synthRef.current.speak(utterance);
    });
  }, [voiceEnabled, rate, pitch, volume, personality, availableVoices, selectedVoiceIndex]);

  // Main speak function - Hybrid approach
  const speak = useCallback(async (text, options = {}) => {
    if (!voiceEnabled) {
      return { spoken: false, reason: 'disabled' };
    }

    // Use AIRA VOCAL if available (higher quality), otherwise fallback to browser
    if (airaVocalAvailable && (provider === VOICE_PROVIDERS.AIRA_VOCAL || provider === VOICE_PROVIDERS.HYBRID)) {
      const result = await speakWithAiraVocal(text, options);
      
      // If AIRA VOCAL fails and we're in hybrid mode, try browser fallback
      if (!result.spoken && provider === VOICE_PROVIDERS.HYBRID) {
        console.log('AIRA VOCAL failed, using browser fallback');
        return speakWithBrowser(text, options);
      }
      return result;
    }

    // Use browser TTS
    return speakWithBrowser(text, options);
  }, [voiceEnabled, airaVocalAvailable, provider, speakWithAiraVocal, speakWithBrowser]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    // Stop AIRA VOCAL audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    // Stop browser TTS
    if (synthRef.current) {
      synthRef.current.cancel();
    }
    setIsSpeaking(false);
    setCurrentText('');
    setAiraVocalLoading(false);
    onSpeakingChangeRef.current?.(false, '');
  }, []);

  // Pause speaking
  const pauseSpeaking = useCallback(() => {
    if (synthRef.current && isSpeaking) {
      synthRef.current.pause();
    }
  }, [isSpeaking]);

  // Resume speaking
  const resumeSpeaking = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.resume();
    }
  }, []);

  // Register callback for speaking state changes (for visualizer)
  const onSpeakingChange = useCallback((callback) => {
    onSpeakingChangeRef.current = callback;
    return () => {
      onSpeakingChangeRef.current = null;
    };
  }, []);

  // Get current voice info
  const getCurrentVoiceInfo = useCallback(() => {
    if (availableVoices.length > 0 && selectedVoiceIndex < availableVoices.length) {
      const voice = availableVoices[selectedVoiceIndex];
      return {
        name: voice.name,
        lang: voice.lang,
        local: voice.localService,
        default: voice.default
      };
    }
    return null;
  }, [availableVoices, selectedVoiceIndex]);

  // Test voice
  const testVoice = useCallback(() => {
    const langConfig = getCurrentLanguage();
    const testMessages = {
      en: "Hello! I'm AIRA, your AI assistant. How can I help you today?",
      hi: "नमस्ते! मैं AIRA हूं, आपकी AI सहायक। आज मैं आपकी कैसे मदद कर सकती हूं?",
      ur: "السلام علیکم! میں AIRA ہوں، آپ کی AI معاون۔ آج میں آپ کی کیسے مدد کر سکتی ہوں؟",
      es: "¡Hola! Soy AIRA, tu asistente de IA. ¿Cómo puedo ayudarte hoy?",
      ja: "こんにちは！私はAIRAです、あなたのAIアシスタントです。今日はどのようにお手伝いできますか？",
      ru: "Привет! Я AIRA, ваш AI-помощник. Чем я могу помочь вам сегодня?",
      bn: "নমস্কার! আমি AIRA, আপনার AI সহকারী। আজ আমি কীভাবে আপনাকে সাহায্য করতে পারি?",
      mr: "नमस्कार! मी AIRA आहे, तुमची AI सहाय्यक. आज मी तुम्हाला कशी मदत करू शकते?",
      pa: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ AIRA ਹਾਂ, ਤੁਹਾਡੀ AI ਸਹਾਇਕ। ਅੱਜ ਮੈਂ ਤੁਹਾਡੀ ਕਿਵੇਂ ਮਦਦ ਕਰ ਸਕਦੀ ਹਾਂ?",
      ta: "வணக்கம்! நான் AIRA, உங்கள் AI உதவியாளர். இன்று நான் உங்களுக்கு எவ்வாறு உதவ முடியும்?",
      de: "Hallo! Ich bin AIRA, Ihr KI-Assistent. Wie kann ich Ihnen heute helfen?",
      te: "నమస్కారం! నేను AIRA, మీ AI సహాయకురాలు. ఈ రోజు నేను మీకు ఎలా సహాయం చేయగలను?",
      ml: "നമസ്കാരം! ഞാൻ AIRA ആണ്, നിങ്ങളുടെ AI അസിസ്റ്റന്റ്. ഇന്ന് ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കും?",
      ar: "مرحباً! أنا AIRA، مساعدك الذكي. كيف يمكنني مساعدتك اليوم؟",
      fr: "Bonjour! Je suis AIRA, votre assistant IA. Comment puis-je vous aider aujourd'hui?",
      pt: "Olá! Eu sou AIRA, sua assistente de IA. Como posso ajudá-lo hoje?",
      zh: "你好！我是AIRA，你的AI助手。今天我能帮你什么？",
      ko: "안녕하세요! 저는 AIRA입니다, 당신의 AI 어시스턴트입니다. 오늘 어떻게 도와드릴까요?"
    };
    speak(testMessages[langConfig.id] || testMessages.en);
  }, [speak, getCurrentLanguage]);

  const value = {
    // State
    voiceEnabled,
    setVoiceEnabled,
    provider,
    setProvider,
    personality,
    setPersonality,
    accent,
    setAccent,
    gender,
    setGender,
    age,
    setAge,
    rate,
    setRate,
    pitch,
    setPitch,
    volume,
    setVolume,
    isSpeaking,
    speakingProgress,
    currentText,
    
    // AIRA VOCAL status
    airaVocalAvailable,
    airaVocalLoading,
    
    // Language settings
    language,
    setLanguage,
    autoDetectLanguage,
    setAutoDetectLanguage,
    detectedLanguage,
    setDetectedLanguage,
    getCurrentLanguage,
    getSpeechRecognitionLanguage,
    SUPPORTED_LANGUAGES,
    
    // Always Listening / Wake Word
    alwaysListening,
    setAlwaysListening,
    toggleAlwaysListening,
    wakeWord,
    setWakeWord,
    wakeWordDetected,
    isWakeWordListening,
    startWakeWordListening,
    stopWakeWordListening,
    onWakeWordDetected,
    
    // Voice data
    availableVoices,
    selectedVoiceIndex,
    setSelectedVoiceIndex,
    
    // Constants
    VOICE_PERSONALITIES,
    VOICE_ACCENTS,
    VOICE_GENDERS,
    VOICE_PROVIDERS,
    VOICE_AGE_GROUPS,
    
    // Functions
    speak,
    speakWithAiraVocal,
    speakWithBrowser,
    stopSpeaking,
    pauseSpeaking,
    resumeSpeaking,
    applyPersonality,
    onSpeakingChange,
    getCurrentVoiceInfo,
    testVoice
  };

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  );
}

export function useVoice() {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
}

export default VoiceContext;
